import type { SerializeFrom } from '@remix-run/server-runtime'
import { TagIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useShop } from '~/hooks/data/use-shop'
import type { CartFull } from '~/models/cart.server'
import type { ShopPromotion } from '~/schemas/shop'
import { Tag } from '~/ui'
import { centsToEurosFormattedPrice } from '~/utils/number'

interface CartPromotionsProps {
  cart: SerializeFrom<CartFull>
}

export const CartPromotions = ({ cart }: CartPromotionsProps) => {
  const { t } = useTranslation()

  const shop = useShop()
  const promotions = (shop.promotions as ShopPromotion[]) || []

  const totalCartPrice = useMemo(() => {
    return cart.cartLines.reduce((amount, item) => {
      return amount + item.quantity * item.productVariant.price
    }, 0)
  }, [cart])

  return promotions.map((promotion, index) => {
    let label = null
    const minSpend = promotion.minSpend || 0

    if (
      totalCartPrice >= minSpend ||
      !['xPercentOff', 'freeShipping'].includes(promotion.type)
    ) {
      return null
    }

    const remainingSpend = minSpend - totalCartPrice

    if (promotion.type === 'xPercentOff') {
      label = t('components.cart.info.spend_more_x_for_x', {
        amount: centsToEurosFormattedPrice(remainingSpend),
        percentage: promotion.amount,
      })
    } else {
      label = t('components.cart.info.spend_more_x_for_free_shipping', {
        amount: centsToEurosFormattedPrice(remainingSpend),
      })
    }

    return (
      <Tag key={index} className="justify-center">
        <TagIcon className="size-4.5" />
        {label}
      </Tag>
    )
  })
}
